import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import {
    ITaskResponse,
    TaskStatus,
    TReminderLabels,
    TTaskReminder,
    TTaskServerStatuses,
    TTaskSource
} from '~/components/Tasks/types/tasks.types';
import {
    convert24HourTimeStringTo12HourString,
    formatRelativeDate
} from '~/utils/date.utils';
import { TFullTimezone } from '~/utils/timezone.utils';
import { DEFAULT_ADVANCED_FILTERS } from '~/components/Tasks/stores/tasks.store';
import { TTaskGroup } from '~/apps/main/Workspace/TasksTab/workspace-tasks.types';

export const getUpdatedTaskStatus = (task: ITaskResponse) => {
    const status: TaskStatus = task.status as TaskStatus;
    const isOverdue =
        task.is_overdue &&
        status !== TaskStatus.COMPLETED &&
        status !== TaskStatus.APPROVED;
    const isApprovalPending =
        (task.status === TaskStatus.PENDING ||
            task.status === TaskStatus.REQUIRED) &&
        task.type !== 'task' &&
        task.source === 'workflow';

    if (isOverdue && isApprovalPending) {
        return TaskStatus.OVERDUE_APPROVAL_PENDING;
    }

    if (isOverdue) {
        return TaskStatus.OVERDUE;
    }

    if (isApprovalPending) {
        return TaskStatus.APPROVAL_PENDING;
    }

    return status;
};

export const toggleUpdatedTaskStatusObject = (
    currentStatus: TTaskServerStatuses,
    taskSource: TTaskSource
): { status: TTaskServerStatuses; prevStatus: TTaskServerStatuses } => {
    if (
        currentStatus === TaskStatus.PENDING ||
        currentStatus === TaskStatus.REQUIRED
    ) {
        return {
            status: TaskStatus.COMPLETED,
            prevStatus: currentStatus
        };
    } else if (
        (taskSource !== 'workflow' && currentStatus === TaskStatus.COMPLETED) ||
        currentStatus === TaskStatus.APPROVED
    ) {
        return { status: TaskStatus.PENDING, prevStatus: currentStatus };
    }
};

export const reminderLabelToValueMap: Record<TReminderLabels, TTaskReminder> = {
    '0_minutes': { unit_type: 'minutes', unit_count: 0 },
    '5_minutes': { unit_type: 'minutes', unit_count: 5 },
    '10_minutes': { unit_type: 'minutes', unit_count: 10 },
    '15_minutes': { unit_type: 'minutes', unit_count: 15 },
    '1_hours': { unit_type: 'hours', unit_count: 1 },
    '2_hours': { unit_type: 'hours', unit_count: 2 },
    '1_days': { unit_type: 'days', unit_count: 1 },
    '2_days': { unit_type: 'days', unit_count: 2 }
};

export const getReminderValue = (
    reminder?: TTaskReminder | null
): TReminderLabels => {
    if (!reminder) {
        return null;
    }
    return `${reminder.unit_count}_${reminder.unit_type}` as TReminderLabels;
};

// exact time the reminder will be triggered based on task due date&time and reminder setting
export const calculateAbsoluteReminderTime = ({
    reminder,
    date,
    time
}: {
    reminder: TTaskReminder;
    date: string;
    time: string;
}) => {
    if (!reminder) {
        return null;
    }
    const { unit_count, unit_type } = reminder;
    const dateTime = dayjs(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
    const reminderDateTime = dateTime.subtract(unit_count, unit_type);
    return reminderDateTime.format('MMMM D, YYYY, h:mm A');
};

export const getInitialTaskDateTime = ({
    dueDate,
    dueTime,
    timezone = 'America/Los_Angeles'
}: {
    dueDate?: string;
    dueTime?: string;
    timezone?: TFullTimezone;
}) => {
    if (dueDate) {
        return {
            date: dayjs.tz(dueDate, 'YYYY-MM-DD', timezone),
            time: dueTime || null
        };
    }

    const nextFullHour = dayjs().tz(timezone).startOf('hour').add(1, 'hour');

    return {
        date: nextFullHour.startOf('day'),
        time: nextFullHour.format('HH:mm')
    };
};

export const formatDueDateAndTime = (
    dueDate?: string,
    dueTime?: string
): string => {
    if (!dueDate) {
        return '';
    }

    const formattedDate = formatRelativeDate({
        date: dueDate,
        withoutYear: true,
        dateFormat: 'MMM D'
    });

    const formattedTime = dueTime
        ? `, ${convert24HourTimeStringTo12HourString(dueTime)}`
        : '';

    return `${capitalize(formattedDate)}${formattedTime}`;
};

export function getFiltersCount(filters: Record<string, any>) {
    return Object.entries(filters).reduce(
        (acc: number, [key, filter]: [string, any]) => {
            if (Array.isArray(filter)) {
                return acc + filter.length;
            }
            if (filter instanceof Set) {
                return acc + filter.size;
            }
            // Don't count filters that match their default values
            if (DEFAULT_ADVANCED_FILTERS[key] === filter) {
                return acc;
            }
            return acc + (filter ? 1 : 0);
        },
        0
    );
}

export function convertFlatSetsToArrays(
    obj: Record<string, any>
): Record<string, any> {
    const result: Record<string, any> = {};

    for (const key in obj) {
        const value = obj[key];
        result[key] = value instanceof Set ? [...value] : value;
    }

    return result;
}

const taskCleanupSelectors = [
    '[data-testid*="assignee"]',
    '[data-testid*="popover"]',
    '[data-testid*="dropdown"]',
    '[data-testid*="menu"]',
    '[data-testid*="description"]',
    '[data-testid*="cell-value"]',
    '[data-testid*="due-date"]'
];

export const isFormInteraction = (target: HTMLElement): boolean => {
    // Interactive attributes
    if (
        target.hasAttribute('role') ||
        target.hasAttribute('aria-expanded') ||
        target.hasAttribute('contenteditable')
    ) {
        return true;
    }

    // shouldn't cleanup when clicking on these selectors
    return taskCleanupSelectors.some(
        selector => target.closest(selector) !== null
    );
};

export const isTaskDueThisWeek = (task: ITaskResponse): boolean => {
    if (!task.due_date) {
        return false;
    }

    const today = dayjs().startOf('day');
    const oneWeekFromToday = today.add(8, 'days');
    const taskDueDate = dayjs(task.due_date).startOf('day');

    return taskDueDate.isBefore(oneWeekFromToday);
};

export const taskHasMeaningfulContent = (
    task: ITaskResponse,
    currentUserId?: string
): boolean => {
    const isDefaultAssignee = task.assignee?._id === currentUserId;
    const isDefaultDescription =
        !task.description ||
        task.description === 'Task name' ||
        task.description.trim() === '';

    return (
        !isDefaultDescription ||
        !!task.due_date ||
        !!task.due_time ||
        !!task.task_group_id ||
        !isDefaultAssignee
    );
};

export const taskGroupInfo = (
    taskGroups: TTaskGroup[],
    taskGroupId: string
) => {
    return taskGroups?.find(group => group._id === taskGroupId);
};
